import type { FilterOption } from '@/components/common-table/FilterSearchBar'
import { isWithinTimeRange } from '@/config/utils'

/**
 * Filters that actually constrain rows. Drops empty value placeholders (e.g. after
 * clearing all values in a multi-select) so behavior matches having no value filter yet.
 * Keeps is_null / is_not_null which do not rely on a non-empty value string.
 */
export function getEffectiveFilters(filters: FilterOption[]): FilterOption[] {
	return filters.filter(f => {
		if (f.operator === 'is_null' || f.operator === 'is_not_null') {
			return true
		}
		return String(f.value ?? '').trim() !== ''
	})
}

export function applyFilterLogic<T>(
	data: T[],
	filters: FilterOption[],
	getItemValue: (item: T, column: string) => unknown,
): T[] {
	const effectiveFilters = getEffectiveFilters(filters)
	if (effectiveFilters.length === 0) {
		return data
	}

	const groupedFilters = effectiveFilters.reduce<Record<string, FilterOption[]>>((acc, f) => {
		if (!acc[f.column]) {
			acc[f.column] = []
		}
		acc[f.column].push(f)
		return acc
	}, {})

	return data.filter(item => {
		return Object.entries(groupedFilters).every(([column, colFilters]) => {
			const itemValue = getItemValue(item, column)

			if (Array.isArray(itemValue)) {
				return colFilters.some(filter => {
					const filterValue = filter.value.toLowerCase().trim()

					switch (filter.operator) {
						case 'equals':
							return itemValue.some(val => String(val).toLowerCase().trim() === filterValue)
						case 'notEquals':
							return !itemValue.some(val => String(val).toLowerCase().trim() === filterValue)
						case 'contains':
							return itemValue.some(val => String(val).toLowerCase().trim().includes(filterValue))
						case 'is_not_null':
							return itemValue.length > 0
						case 'is_null':
							return itemValue.length === 0
						default:
							// For other operators, check if array contains the filter value
							return itemValue.some(val => String(val).toLowerCase().trim() === filterValue)
					}
				})
			}

			const itemValueStr =
				itemValue !== null && itemValue !== undefined
					? typeof itemValue === 'object'
						? JSON.stringify(itemValue)
						: String(itemValue)
					: ''

			const normalizedValue = itemValueStr.toLowerCase().trim()

			return colFilters.some(filter => {
				const filterValue = filter.value.toLowerCase().trim()

				switch (filter.operator) {
					case 'equals':
						return normalizedValue === filterValue
					case 'notEquals':
						return normalizedValue !== filterValue
					case 'contains':
						return normalizedValue.includes(filterValue)
					case 'is_not_null':
						return itemValue !== null && itemValue !== undefined && itemValue !== ''
					case 'is_null':
						return itemValue === null || itemValue === undefined || itemValue === ''
					case 'timeRange':
						return isWithinTimeRange(itemValue as string | number | Date, filter.value)
					case '<':
						return Number(itemValue) < Number(filterValue)
					case '<=':
						return Number(itemValue) <= Number(filterValue)
					case '>':
						return Number(itemValue) > Number(filterValue)
					case '>=':
						return Number(itemValue) >= Number(filterValue)
					default:
						return true
				}
			})
		})
	})
}

/**
 * Free text search across every value of a row, then the column filters on what survives.
 * The search is deliberately naive: it stringifies each value, so it also matches on fields
 * the table does not show.
 */
export function filterBySearchAndFilters<T>(
	data: T[],
	searchValue: string,
	filters: FilterOption[],
	getItemValue: (item: T, column: string) => unknown,
): T[] {
	let result = data

	if (searchValue) {
		const needle = searchValue.toLowerCase()
		result = result.filter(item =>
			Object.values(item as Record<string, unknown>).some(value => String(value).toLowerCase().includes(needle)),
		)
	}

	return filters.length > 0 ? applyFilterLogic(result, filters, getItemValue) : result
}
